import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import 'react-datepicker/dist/react-datepicker.css';
import { addDays, startOfWeek, format } from 'date-fns';
import './TimesheetViewPage.css';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;

const statusColors = {
  draft: '#bdbdbd',
  pending: '#f1c40f',
  approved: '#388e3c',
  rejected: '#d32f2f',
};

// Map status number to string
const statusMap = { 
  1: 'draft', 
  2: 'pending', 
  3: 'approved', 
  4: 'rejected' 
};

const TimesheetViewPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useSelector((state) => state.auth);
  const [form, setForm] = useState({});
  const [weekStart] = useState(new Date());
  const [weekData, setWeekData] = useState({
    st: Array(7).fill(''),
    ot: Array(7).fill(''),
  });
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get timesheet data from navigation state if available
  const navState = location.state && location.state.timesheet;
  const id = paramId;

  useEffect(() => {
    let ignore = false;
    async function fetchTimesheet() {
      setLoading(true);
      setError(null);
      try {
        if (navState && navState.id === id) {
          const ts = navState;
          setForm({
            fromDate: ts.fromDate ? new Date(ts.fromDate) : null,
            toDate: ts.toDate ? new Date(ts.toDate) : null,
            approver: ts.approver || '-',
            project: ts.project || '-',
            standardTime: ts.standardTime || '-',
            overtime: ts.overtime || '-',
            status: ts.status || '-',
            orgName: ts.department?.costCenter?.organization?.orgName || '-',
            ccName: ts.department?.costCenter?.ccName || '-',
            department: ts.department?.deptName || '-',
            user: `${ts.user?.firstName || ""} ${ts.user?.lastName || ""}`.trim() || "-",
          });
          setLoading(false);
          return;
        }
        if (!token || !user || !id) throw new Error('Missing auth or id');
        const response = await axios.get(
          `${API_BASE_URL}timesheet/all?userId=&projectId=&from=&to=&orgId=${user.department?.costCenter?.organization?.orgId}&approverId=${user.userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const apiData = response.data || [];
        const found = apiData.find(ts => String(ts.tsId) === String(id));
        if (!found) throw new Error('Timesheet not found');

        // Map weekData from timeSheetDetailList
        const stArr = Array(7).fill('');
        const otArr = Array(7).fill('');
        if (Array.isArray(found.timeSheetDetailList)) {
          

          found.timeSheetDetailList.forEach(detail => {
            //console.log(detail.standardTimeAmt);
            //console.log(detail.overTimeAmt)
            console.log(detail.tsId)
            const idx = (detail.dayOfWeek - 1); // dayOfWeek: 1=Mon, 7=Sun
            stArr[idx] = detail.standardTimeAmt != null ? detail.standardTimeAmt : '';
            otArr[idx] = detail.overTimeAmt != null ? detail.overTimeAmt : '';
          });
        }

        setForm({
          fromDate: found.dateFrom ? new Date(found.dateFrom) : null,
          toDate: found.dateTo ? new Date(found.dateTo) : null,
          approver: found.approver
            ? `${found.approver.firstName || ''} ${found.approver.lastName || ''}`.trim()
            : '',
          project: found.project ? found.project.projectCode : '',
          standardTime: found.stSubtotal || '',
          overtime: found.otSubtotal || '',
          status: statusMap[found.status] || 'draft',
          orgName: found.department?.costCenter?.organization?.orgName || '-',
          ccName: found.department?.costCenter?.ccName || '-',
          department: found.department?.deptName || '-',
          user: `${found.user?.firstName || ""} ${found.user?.lastName || ""}`.trim() || "-",
        });
        setWeekData({ st: stArr, ot: otArr });
        setComment(found.description || '');
      } catch (err) {
        setError(err.message || 'Error loading timesheet');
      } finally {
        setLoading(false);
      }
    }
    fetchTimesheet();
    return () => { ignore = true; };
  }, [id, token, user, navState]);

  // Helper to get week dates
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(weekStart, { weekStartsOn: 1 }), i));
  // Day names for translation
  const dayNames = [
    t('approvalsViewPage.mon', 'Mon'),
    t('approvalsViewPage.tue', 'Tue'),
    t('approvalsViewPage.wed', 'Wed'),
    t('approvalsViewPage.thu', 'Thu'),
    t('approvalsViewPage.fri', 'Fri'),
    t('approvalsViewPage.sat', 'Sat'),
    t('approvalsViewPage.sun', 'Sun'),
  ];
  const stTotal = weekData.st.reduce((sum, v) => sum + (parseFloat(v) || 0.0), 0);
  const otTotal = weekData.ot.reduce((sum, v) => sum + (parseFloat(v) || 0.0), 0);
  const dayTotals = weekData.st.map((st, i) => (parseFloat(st) || 0.0) + (parseFloat(weekData.ot[i]) || 0.0));
  const weekTotal = stTotal + otTotal;

  const formatTotal = (num) => num ? num.toFixed(2) : '0.00';

  if (loading) {
    return <div className="ts-edit-container">{t('loading', 'Loading...')}</div>;
  }
  if (error) {
    return <div className="ts-edit-container" style={{ color: 'red' }}>{t('error', error)}</div>;
  }

  return (
    <div className="ts-edit-container">
      <h2 className="ts-edit-title">{t('approvalsViewPage.timesheetInfo')}</h2>
      {
        <div className="ts-edit-info-row">
          <div className="ts-edit-status" style={{ background: statusColors[form.status] || '#bdbdbd', display: 'inline-block', marginRight: 12 }}>
            {t('approvalsViewPage.status')}: {t(`approvalsViewPage.${form.status}`, form.status.charAt(0).toUpperCase() + form.status.slice(1))}
          </div>
          {id && (
            <span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'top', marginRight: 16 }}>
              {t('approvalsViewPage.id')}: {id}
            </span>
          )}
          <span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'top', marginRight: 16 }}>
            {t('approvalsViewPage.org')}: {form.orgName || '-'}
          </span>
          <span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'top', marginRight: 16 }}>
            {t('approvalsViewPage.costCenter')}: {form.ccName || '-'}
          </span>
          <span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'top', marginRight: 16 }}>
            {t('approvalsViewPage.dept')}: {form.department || '-'}
          </span>
          <span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'top', marginRight: 16 }}>
            {t('approvalsViewPage.approver')}: {form.approver || '-'}
          </span>
          <span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'top', marginRight: 16 }}>
            {t('approvalsViewPage.user')}: {form.user || '-'}
          </span>
        </div>
      }

      {/* Project / From / To row */}
      <div className="ts-edit-project-date-row">
        {/* Left: Project dropdown */}
        <div className="ts-edit-project">
          <label className="ts-edit-label-inline" htmlFor="project-select">
            {t('approvalsViewPage.project') || 'Project'}:
          </label>
          <input
            type="text"
            name="project"
            value={form.project}
            disabled={true}
            className="ts-edit-input ts-edit-project-select"
          />
        </div>

        {/* Right: From + To date pickers */}
        <div className="ts-edit-dates">
          <div className="ts-edit-row-item">
            <label className="ts-edit-label-inline">{t('approvalsViewPage.fromDate')}</label>
            <DatePicker
              selected={form.fromDate}
              dateFormat="yyyy-MM-dd"
              className="ts-edit-date-picker"
              placeholderText={t('approvalsViewPage.selectStartDate')}
              disabled={true}
            />
          </div>

          <div className="ts-edit-row-item">
            <label className="ts-edit-label-inline">{t('approvalsViewPage.toDate')}</label>
            <DatePicker
              selected={form.toDate}
              dateFormat="yyyy-MM-dd"
              className="ts-edit-date-picker"
              placeholderText={t('approvalsViewPage.selectEndDate')}
              disabled={true}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="ts-edit-table-wrapper">
        <table className="ts-edit-table">
          <thead>
            <tr>
              <th className="ts-edit-th" style={{ width: 80 }}>{t('approvalsViewPage.day')}</th>
              {weekDates.map((date, i) => (
                <th className="ts-edit-th" key={i}>
                  <div>{dayNames[i]}</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: 2 }}>{format(date, 'yyyy-MM-dd')}</div>
                </th>
              ))}
              <th className="ts-edit-th">{t('approvalsViewPage.total')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="ts-edit-row-label">{t('approvalsViewPage.st')}</td>
              {weekData.st.map((v, i) => (
                <td className="ts-edit-td" key={i}>
                  <input type="number" min="0" step="0.1" value={v} disabled={true}
                    className="ts-edit-input" />
                </td>
              ))}
              <td className="ts-edit-td ts-edit-total-cell">{formatTotal(stTotal)}</td>
            </tr>
            <tr>
              <td className="ts-edit-row-label">{t('approvalsViewPage.ot')}</td>
              {weekData.ot.map((v, i) => (
                <td className="ts-edit-td" key={i}>
                  <input type="number" min="0" step="0.1" value={v} disabled={true}
                    className="ts-edit-input" />
                </td>
              ))}
              <td className="ts-edit-td ts-edit-total-cell">{formatTotal(otTotal)}</td>
            </tr>
            <tr>
              <td className="ts-edit-row-label">{t('approvalsViewPage.total')}</td>
              {dayTotals.map((v, i) => (
                <td className="ts-edit-td ts-edit-total-cell" key={i}>{formatTotal(v)}</td>
              ))}
              <td className="ts-edit-td ts-edit-grand-total">{formatTotal(weekTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="ts-edit-label">{t('approvalsViewPage.comment')}:</label>
        <textarea
          className="ts-edit-input ts-edit-comment-box"
          value={comment}
          disabled={true}
          placeholder={t('approvalsViewPage.addComment')}
          maxLength={512}
        />
        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
          {comment.length}/512
        </div>
      </div>
      <div className="ts-edit-btn-row">
        <button type="button" onClick={() => navigate('/approvals/timesheetapprovals')} className="ts-edit-btn cancel">
          {t('approvalsViewPage.cancel')}
        </button>
      </div>
    </div>
  );
};

export default TimesheetViewPage;

